
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import Slider from '@/pages/product/components/Slider.vue'
import HowToDesign from '@/pages/product/components/HowToDesign.vue'
import ProductDetails from '@/pages/product/components/ProductDetails.vue'
import OrderFlow from '@/pages/product/components/OrderFlow.vue'
import QuantitySelect from '@/pages/product/components/QuantitySelect.vue'
import PrintingOptions from '@/pages/product/components/PrintingOptions.vue'
import EstimatePrice from '@/pages/product/components/EstimatePrice.vue'
import EstimateInvoice from '@/pages/product/components/EstimateInvoice.vue'
import ProductOptions from '@/pages/product/components/ProductOptions.vue'
import OrderUnitInfo from '@/pages/product/components/OrderUnitInfo.vue'
import PriceTable from '@/pages/product/components/PriceTable.vue'
// import OrderMethod from '@/pages/product/components/OrderMethod.vue'
import SimilarProducts from '@/pages/product/components/SimilarProducts.vue'
import SectionTitle from '@/pages/product/components/SectionTitle.vue'
import ContactLink from '@/pages/product/components/ContactLink.vue'
import PrintableColors from '@/pages/product/components/PrintableColors.vue'
import Skeleton from '@/components/Skeleton.vue'
import DeliveryOptions from '@/pages/product/components/DeliveryOptions.vue'
import Loading from '@/components/Loading.vue'
import { BQ_EVENT_NAMES, raksulBQEventTracker } from '@/utils'

export default Vue.extend({
  components: {
    Slider,
    HowToDesign,
    ProductDetails,
    OrderFlow,
    QuantitySelect,
    PrintingOptions,
    EstimatePrice,
    EstimateInvoice,
    ProductOptions,
    PriceTable,
    OrderUnitInfo,
    // OrderMethod,
    SimilarProducts,
    SectionTitle,
    ContactLink,
    PrintableColors,
    Skeleton,
    DeliveryOptions,
    Loading
  },
  props: {
    isMobileLayout: {
      type: Boolean
    }
  },
  data(): {
    customClass: string
    disabledLoading: boolean,
    isFirstLoad: boolean
  } {
    return {
      customClass: '',
      disabledLoading: false,
      isFirstLoad: true
    }
  },
  computed: {
    ...mapGetters([
      'estimate',
      'product',
      'estimateQuantity',
      'fullRequestQueries',
      'wrappingSelectedOption',
      'selectedColorPatternOption',
      'selectedEditPattern',
      'skuImageView',
      'currentSKUImageView'
    ]),
    routerQueries() {
      return {
        ...this.$route.query,
        psi: this.estimate.printable_sku_id,
        quantity: this.estimateQuantity,
        trial: this.estimate.trial,
        plain: this.estimate.plain_good
      }
    },
    isLoadedProduct(): boolean {
      return this.product && this.product.product_id
    },
    isProductOptionVisible() : boolean {
      return this.product.first_options.length > 0 ||
        this.product.second_options.length > 0 ||
        (this.product.wrapping_options.length > 1 && this.wrappingSelectedOption.id)
    },
    printingSpecific() {
      const printingIndex = this.isProductOptionVisible ? 3 : 2
      return {
        show: (this.product.color_pattern_options.length > 0  && this.selectedColorPatternOption)
          || (this.product.edit_pattern_options.length > 0 && this.selectedEditPattern),
        index: this.hasValidDeliveryOption ? printingIndex + 1 : printingIndex
      }
    },
    finalStepIndex(): number {
      const checkOptionSpecific = (this.isProductOptionVisible || this.printingSpecific.show) ? 3 : 2
      const finalIndex = (this.isProductOptionVisible && this.printingSpecific.show) ? 4 : checkOptionSpecific
      return this.hasValidDeliveryOption ? finalIndex + 1 : finalIndex
    },
    isRefreshingProduct(): boolean {
      if (this.product?.product_id) {
        return this.$store.state.productLoading && !this.disabledLoading
      }
      return false
    },
    isShowInProductOptions(): boolean {
      return this.isProductOptionVisible && !this.printingSpecific.show
    },
    isShowInMethodOptions(): boolean {
      return !this.printingSpecific.show && !this.isShowInProductOptions
    },
    hasQuickDeliveryOption(): boolean {
      return this.product.has_quick_delivery_option && this.product.setting_quick_delivery_change_ui
    },
    hasValidDeliveryOption(): boolean {
      return this.hasQuickDeliveryOption && this.$store.state.delivery_options.length > 0
    },
    isQuickDeliveryFirstOption(): boolean {
      return this.hasQuickDeliveryOption && this.product.setting_quick_delivery_is_first_option
    },
    isQuickDeliverySecondOption(): boolean {
      return this.hasQuickDeliveryOption && this.product.setting_quick_delivery_is_second_option
    },
    productIndex(): number {
      return this.hasValidDeliveryOption ? 2 : 1
    }
  },
  created() {
    const { psi, quantity, trial, plain } = this.$route.query
    const { id } = this.$route.params
    const hasParams = !!psi

    if (plain == 'true') {
      this.$store.commit('setIsOrderPlainProduct', true)
    }

    if (hasParams) {
      this.getEstimateByPsi({ param: psi, queries: { quantity, trial }})
        .then(() => {
          const isPlainPsi = this.estimate.plain_good
          this.$store.commit('setIsOrderPlainProduct', isPlainPsi)
          this.refreshProductInfo({
            param: id
          }, false)
        })
        .catch(() =>
          this.refreshProductInfo({ param: id, queries: {}, keepRoute: false })
        )
    } else {
      this.refreshProductInfo({ param: id, queries: {}, keepRoute: false })
    }
    if (document.referrer.includes(`${window.location.origin}/search`)) {
      this.trackingProductClickFromSearch(id)
    }
  },
  methods: {
    ...mapActions([
      'getEstimateByPsi',
      'getProduct'
    ]),
    refreshProductInfo(payload) {
      const inputData = payload || {}
      const { param, queries , loadImageView, keepRoute, disabledLoading } = inputData
      const psi = this.estimate.printable_sku_id
      this.disabledLoading = disabledLoading ? true : false

      return this.getProduct({
        param: param || this.product.product_id,
        loadImageView: loadImageView || false,
        queries: queries || this.fullRequestQueries
      })
        .then(() => {
          if (!keepRoute) {
          return this.$router.replace({ query: this.routerQueries }).catch(() => undefined)
        } else {
          return Promise.resolve()
        }
      })
      .then(() => {
        this.isFirstLoad = false
        if (this.isQuickDeliveryFirstOption) {
          this.$store.commit('setDeliveryOptions', this.product.first_options)
          this.$store.commit('setFirstOptions',[])
        }
        if (this.isQuickDeliverySecondOption) {
          this.$store.commit('setDeliveryOptions',this.product.second_options)
          this.$store.commit('setSecondOptions',[])
        }
        if (psi !== this.estimate.printable_sku_id || !this.$store.getters.priceTable) {
          // Return the promise from dispatch
          return this.$store.dispatch('getPriceTable')
            .catch(error => {
              // Handle error from getPriceTable
              throw new Error(error)
            })
        } else {
          return Promise.resolve()
        }
      })
      .catch(() => {
        // Handle any errors occurred in the chain possibly  NavigationDuplicated error
        this.isFirstLoad = false
        // throw new Error(error)

      })
    },
    setFirstSlideImage(firstSKUId: number) {
      this.$refs?.productSlider?.onHoverSliderImage(firstSKUId)
    },
    setImageDescription(caption: string) {
      this.$refs.productSlider.changeImageDescription(caption)
    },
    trackingProductClickFromSearch(productId: string) {
      raksulBQEventTracker({
        eventName: BQ_EVENT_NAMES.SEARCH_RESULT_CLICK,
        data: {
          productId: productId
        }
      })
    },
    handleValidateQuantity() {
      this.$refs?.quantitySelect?.$refs.quantityInput.validateQuantity(this.estimateQuantity)
    }
  }
})
